import * as RadixDialog from "@radix-ui/react-dialog";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { CloseIcon, SearchIcon } from "@/components/ui/icons";
import { apiFetch } from "@/lib/api";
import type { Page, SearchResultResource } from "@/lib/api-types";
import { cx } from "@/lib/cx";
import { focusReturnElement, useOverlayGrant, useReturnFocusOnOpen } from "@/lib/overlay-gate";
import { groupSearchResults } from "@/lib/search-groups";
import { useIsPhone } from "@/lib/use-media-query";
import { useDebouncedValue } from "@/lib/use-debounced-value";

/**
 * Story 9.2 — the one bespoke global search overlay. No existing overlay
 * fits as-is: `Dialog.tsx` is a fixed 480px centered card, `Sheet.tsx` never
 * covers the full screen, and choosing between two separately-mounted
 * components by viewport (`ConfirmDialog.tsx`'s own pattern) would remount
 * this one across a resize/orientation change and lose the live query —
 * this is one mounted component, responsive *within itself* via
 * `useIsPhone()`-conditional classes only (spec judgement call 1).
 *
 * Built directly on Radix `Dialog` (not the already-wrapped `Dialog.tsx`/
 * `Sheet.tsx`, whose own `Content` styling is fixed) — the reason this file
 * lives in `components/ui/` rather than beside `NavRail.tsx`/`AppShell.tsx`
 * in `components/shell/`: `radix-import-guard.test.ts` confines every
 * `@radix-ui/*` import to this directory, so every overlay primitive in the
 * app — hand-styled or not — reaches Radix from exactly one place. Registers
 * with `lib/overlay-gate.ts` exactly as `Dialog`/`Sheet`/`Popover` already
 * do (VC-9.2-09): "no overlay opens on top of another" holds here too.
 *
 * Keyboard mechanics mirror `Combobox.tsx`'s own WAI-ARIA "combobox with
 * list autocomplete" pattern (`role="combobox"`, `aria-activedescendant`,
 * `role="listbox"`/`role="option"`): DOM focus never leaves the input,
 * results are grouped only visually (`lib/search-groups.ts`), and Up/Down
 * traverse the one flattened, ordered list (AC: "move between results," not
 * "between groups"). One real deviation from `Combobox.tsx`'s own row
 * `onMouseDown` + `preventDefault` trick: that trick exists to stop a
 * `Popover`'s focus-outside dismiss from racing the click (`Popover.tsx`'s
 * own doc comment) — this overlay's result list is not conditionally shown
 * on focus at all (it stays visible for as long as the overlay itself is
 * open), so there is no equivalent race to guard against, and a plain
 * `onClick` is simpler and exercised directly by a real `fireEvent.click`.
 *
 * Default highlight is the *first* result, not `null` (`Combobox.tsx`'s own
 * closed-list convention) — the I/O matrix's own "pressing ↓ twice then
 * Enter opens the third result" only holds if the first result is already
 * highlighted before any key is pressed (0 -> 1 -> 2 across two presses).
 */

const SEARCH_DEBOUNCE_MS = 180;

export interface SearchOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Wraps the exact synchronous `.focus()` call this component makes to
   * return focus to whichever trigger opened it (`Dialog.tsx`'s identical
   * `onCloseAutoFocus`/`focusReturnElement` pattern). Optional — plain
   * callers get a plain `target.focus()`, same as `Dialog`/`Sheet`.
   *
   * `AppShell.tsx` supplies one: found live, `NavRail`'s own search box
   * opens the overlay on `onFocus` (spec judgement call 4) — when that box
   * is the trigger being refocused here, the very `.focus()` call below
   * fires that same `onFocus` handler and reopens the overlay it is in the
   * middle of closing. `restoreFocus` below is called synchronously inside
   * whatever bracket the caller wants around it, so the caller can suppress
   * exactly the one reopen this produces, with no timing guess (a
   * `setTimeout`-based suppression was tried first and lost a real race
   * against Radix's own deferred focus scheduling — this replaces it).
   */
  onFocusReturn?: (restoreFocus: () => void) => void;
}

function emptyStateMessage(query: string): string {
  return `No matches for "${query}." Check the spelling, or try a shorter word — archived properties are excluded by default.`;
}

export function SearchOverlay({ open, onOpenChange, onFocusReturn }: SearchOverlayProps) {
  const isPhone = useIsPhone();
  const navigate = useNavigate();
  const granted = useOverlayGrant(open);
  const returnFocusTo = useReturnFocusOnOpen(open);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const trimmedQuery = debouncedQuery.trim();

  // Closing (or never having opened) always leaves the next open with a
  // blank box, not the previous search (VC-9.2-11, AC: "closing it leaves
  // that screen unchanged" extended to the overlay's own scratch state).
  // This component stays mounted across opens/closes (`AppShell.tsx` toggles
  // only `open`), so nothing else would ever clear this state.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setHighlighted(null);
    }
  }, [open]);

  const searchQuery = useQuery({
    queryKey: ["search", trimmedQuery],
    queryFn: () =>
      apiFetch<Page<SearchResultResource>>(`/search?q=${encodeURIComponent(trimmedQuery)}&include_archived=false`),
    // Mirrors `app.domain.search.search`'s own empty-query short-circuit
    // (VC-9.1-06): no request at all for an empty box, not a request that
    // comes back empty.
    enabled: trimmedQuery.length > 0,
  });

  // Memoized so its identity only changes when the inputs that should
  // actually change it do — `results` otherwise being a fresh `[]`/`.data`
  // read on every render would re-trigger the highlight-reset effect below
  // on every keystroke's re-render, snapping the highlight back to 0 mid
  // arrow-key navigation.
  const results = useMemo(
    () => (trimmedQuery === "" ? [] : (searchQuery.data?.data ?? [])),
    [trimmedQuery, searchQuery.data],
  );
  const groups = useMemo(() => groupSearchResults(results), [results]);

  useEffect(() => {
    setHighlighted(results.length > 0 ? 0 : null);
  }, [results]);

  const activeDescendant = highlighted !== null ? `${listboxId}-${highlighted}` : undefined;

  const showEmptyState = trimmedQuery !== "" && searchQuery.isSuccess && results.length === 0;
  const announcement = trimmedQuery === "" || searchQuery.isPending
    ? ""
    : results.length === 0
      ? emptyStateMessage(trimmedQuery)
      : `${results.length} result${results.length === 1 ? "" : "s"} found.`;

  function selectResult(result: SearchResultResource) {
    // One handler, mirroring `MoreSheet.tsx`/`NavItem.tsx`'s own
    // `onNavigate` pattern (spec judgement call 7) — never two
    // separately-coordinated steps.
    onOpenChange(false);
    navigate(result.url);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (results.length === 0) {
        return;
      }
      setHighlighted((current) => (current === null ? 0 : current >= results.length - 1 ? 0 : current + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (results.length === 0) {
        return;
      }
      setHighlighted((current) => (current === null ? results.length - 1 : current <= 0 ? results.length - 1 : current - 1));
    } else if (event.key === "Enter") {
      if (highlighted === null) {
        return;
      }
      const result = results[highlighted];
      if (result) {
        event.preventDefault();
        selectResult(result);
      }
    }
    // Esc is not handled here — Radix's own `Dialog.Content` already closes
    // on Escape by default (this overlay never sets `dismissible={false}`
    // the way `ConfirmDialog.tsx` does), so a second, hand-rolled listener
    // would only duplicate what Radix already provides (spec judgement
    // call 6).
  }

  return (
    <RadixDialog.Root open={granted} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-scrim" />
        <RadixDialog.Content
          onOpenAutoFocus={(event) => {
            // Explicit rather than relying on Radix's own default-focusable
            // behavior (VC-9.2-12: "focus moves into the overlay's own
            // input on open") — deterministic regardless of which trigger
            // opened it.
            event.preventDefault();
            inputRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            // See `Dialog.tsx`'s identical comment/`focusReturnElement`'s
            // own doc — Radix's built-in Trigger-based focus-return has
            // nothing to restore to here either, since this overlay's own
            // triggers (`NavRail`'s search box, `PhoneTopBar`'s search
            // button) are plain caller-owned controls, not Radix Triggers.
            const target = focusReturnElement(returnFocusTo);
            if (target) {
              event.preventDefault();
              if (onFocusReturn) {
                onFocusReturn(() => target.focus());
              } else {
                target.focus();
              }
            }
          }}
          aria-describedby={undefined}
          className={cx(
            "fixed z-50 flex flex-col bg-surface shadow-overlay",
            isPhone
              ? "inset-0"
              : "left-1/2 top-16 max-h-[70vh] w-[90%] max-w-search-overlay-max -translate-x-1/2 rounded-lg",
          )}
        >
          <RadixDialog.Title className="sr-only">Search</RadixDialog.Title>
          <div className="flex shrink-0 items-center gap-2 border-b border-border-hairline px-4">
            <SearchIcon className="shrink-0 text-ink-tertiary" />
            <input
              ref={inputRef}
              role="combobox"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-label="Search"
              {...(activeDescendant !== undefined ? { "aria-activedescendant": activeDescendant } : {})}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search properties, units, tenants, documents"
              className="h-search-overlay-row min-w-0 flex-1 border-0 bg-transparent text-body text-ink placeholder-ink-tertiary"
            />
            <button
              type="button"
              aria-label="Close search"
              onClick={() => onOpenChange(false)}
              className="flex h-tap-target-min w-tap-target-min shrink-0 items-center justify-center rounded-md text-ink-secondary hover:bg-surface-hover"
            >
              <CloseIcon />
            </button>
          </div>

          {/* UX-DR20: announces the result count as it changes, for anyone
           * not watching the visible list update. */}
          <p aria-live="polite" className="sr-only">
            {announcement}
          </p>

          <div className="flex-1 overflow-y-auto">
            {showEmptyState ? (
              <div className="px-4 py-12 text-center">
                <p className="text-body text-ink-secondary">{emptyStateMessage(trimmedQuery)}</p>
              </div>
            ) : (
              <ul id={listboxId} role="listbox" aria-label="Search results">
                {groups.map((group) => (
                  <li key={group.slug} role="presentation">
                    <p role="presentation" className="px-4 pb-1 pt-3 text-label font-medium text-ink-secondary">
                      {group.label}
                    </p>
                    <ul role="presentation">
                      {group.items.map(({ result, index }) => {
                        const isHighlighted = index === highlighted;
                        return (
                          <li
                            key={`${result.slug}-${result.url}`}
                            id={`${listboxId}-${index}`}
                            role="option"
                            aria-selected={isHighlighted}
                            onMouseEnter={() => setHighlighted(index)}
                            onClick={() => selectResult(result)}
                            className={cx(
                              "flex h-search-overlay-row min-h-tap-target-min cursor-pointer items-center px-4 text-body text-ink",
                              isHighlighted ? "bg-surface-hover" : undefined,
                            )}
                          >
                            {result.label}
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
